import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  formFlowChromeColumnClassName,
  formFlowStepsColumnClassName,
  formFlowSurfaceClassName,
} from "@/components/forms/formFlowLayout";
import { LogoMark } from "@/components/layout/Sidebar";
import { SkeletonFormFieldBlocks } from "@/components/skeleton-page/SkeletonPage";
import { Heading } from "@/components/ui/heading";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export type FormFlowSkeletonProps = {
  /** Matches {@link FormFlow} multi-step layout (form column + steps aside). */
  hasSteps?: boolean;
  /** Number of sidebar steps shown in the skeleton. */
  stepCount?: number;
  className?: string;
  children?: ReactNode;
};

function SkeletonMobileStepBar(): ReactElement {
  return (
    <div className={cn(formFlowStepsColumnClassName, "mb-4 lg:hidden")}>
      <div className="flex min-w-0 items-center gap-3">
        <Skeleton className="size-7 shrink-0 rounded-full" />
        <Skeleton className="h-4 min-w-0 flex-1" />
        <Skeleton className="size-8 shrink-0 rounded-md" />
      </div>
      <div className="mt-3 flex gap-1">
        <Skeleton className="h-1 flex-1 rounded-full" />
        <Skeleton className="h-1 flex-1 rounded-full" />
      </div>
    </div>
  );
}

function SkeletonFormSteps({ stepCount }: { stepCount: number }): ReactElement {
  return (
    <ol className="list-none space-y-4">
      {Array.from({ length: stepCount }, (_, index) => (
        <li key={index}>
          <div className="flex items-center gap-3 rounded-lg px-3 py-2.5">
            <Skeleton className="size-7 shrink-0 rounded-full" />
            <Skeleton className="h-4 w-24" />
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Loading skeleton aligned with fullscreen {@link FormFlow} chrome and column layout. */
export function FormFlowSkeleton({
  hasSteps = true,
  stepCount = 2,
  className,
  children,
}: FormFlowSkeletonProps): ReactElement {
  const { t } = useTranslation();
  const chromeColumnCls = formFlowChromeColumnClassName(hasSteps);
  const formBody = children ?? <SkeletonFormFieldBlocks />;

  return (
    <div
      className={cn("flex min-h-0 w-full max-w-none flex-1 flex-col", className)}
      aria-busy="true"
    >
      <span className="sr-only">{t("common.loading")}</span>

      <header className="flex min-h-14 shrink-0 border-b border-border bg-background/95 py-2 backdrop-blur supports-backdrop-filter:bg-background/90 sm:py-0">
        <div className={cn(chromeColumnCls, "flex min-h-14 items-stretch")}>
          <div className="flex min-h-14 w-full min-w-0 items-center justify-between gap-3 sm:gap-4">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <LogoMark className="size-7 shrink-0" />
              <Heading variant="chrome" as="span" className="truncate">
                KonstruLog
              </Heading>
            </div>
            <Skeleton className="size-9 shrink-0 rounded-md" />
          </div>
        </div>
      </header>

      <div className="form-flow-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain bg-background">
        <div className="form-flow-scroll-body min-w-0 py-4">
          <div className={chromeColumnCls}>
            <div className="min-w-0 pb-4 sm:pb-5">
              <Skeleton className="h-8 w-72 max-w-full rounded-md sm:h-9" />
            </div>
            {hasSteps ? (
              <>
                <SkeletonMobileStepBar />
                <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr] lg:items-start">
                  <div className={formFlowSurfaceClassName}>
                    <div className="min-w-0 space-y-6">{formBody}</div>
                  </div>
                  <div
                    className={cn(
                      formFlowStepsColumnClassName,
                      "hidden h-fit w-full lg:block lg:sticky lg:top-6 lg:self-start",
                    )}
                  >
                    <SkeletonFormSteps stepCount={stepCount} />
                  </div>
                </div>
              </>
            ) : (
              <div className={formFlowSurfaceClassName}>
                <div className="min-w-0 space-y-6">{formBody}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="flex w-full shrink-0 justify-center border-t border-border bg-background pb-[env(safe-area-inset-bottom,0px)] backdrop-blur supports-backdrop-filter:bg-background/90">
        <div
          className={cn(
            chromeColumnCls,
            "flex min-h-14 w-full min-w-0 items-center justify-end gap-3 py-2",
          )}
        >
          <Skeleton className="h-10 w-28 rounded-md" />
        </div>
      </footer>
    </div>
  );
}
